import AuthenticatedClient from './clients/AuthenticatedClient';
const Client = AuthenticatedClient;

const resource = '/statistics';

function buildQueryString(params) {
  return Object.entries(params)
    .filter(([_, value]) => value !== null && value !== undefined)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
}

/**
 * Statistics / market-data endpoints (`/statistics`, plus `/news`).
 * Methods return the raw AxiosResponse; callers read `.data`.
 */
export default {
  /** GIE gas-storage data for a country. @param {object} payload @returns {Promise<import('axios').AxiosResponse>} */
  getGIEForCountry(payload) {
    return Client.post(`${resource}/gie`, payload);
  },
  /** ENTSO-E day-ahead price moving average. @param {object} payload @returns {Promise<import('axios').AxiosResponse>} */
  getENTSOEDayAheadMovingAverageForCountry(payload) {
    return Client.post(`${resource}/entsoe/day-ahead-prices/moving-average`, payload);
  },
  /** ICE futures series. @param {object} payload @returns {Promise<import('axios').AxiosResponse>} */
  getICEFutures(payload) {
    const params = {
      ice_index: payload.ice_index,
      start_date: payload.start_date,
      end_date: payload.end_date,
    };
    return Client.get(`${resource}/ice?${buildQueryString(params)}`);
  },
  /** ICE daily prices. @param {object} payload @returns {Promise<import('axios').AxiosResponse>} */
  getICEDaily(payload) {
    const params = {
      ice_index: payload.ice_index,
      start_date: payload.start_date,
      end_date: payload.end_date,
      daily_type: payload.daily_type,
      date_selection: payload.date_selection,
    };
    return Client.get(`${resource}/ice/daily?${buildQueryString(params)}`);
  },
  /** ICE settlement prices. @param {object} payload @returns {Promise<import('axios').AxiosResponse>} */
  getICESettlements(payload) {
    const params = {
      ice_index: payload.ice_index,
      start_date: payload.start_date,
      end_date: payload.end_date,
    };
    return Client.get(`${resource}/ice/settlements?${buildQueryString(params)}`);
  },
  /** Live Dutch TTF ICE price. @returns {Promise<import('axios').AxiosResponse>} */
  getICELive() {
    return Client.get(`${resource}/ice/live/dutch-ttf`);
  },
  /** Day-ahead prices. @param {object} payload @returns {Promise<import('axios').AxiosResponse>} */
  getDayAheadPrices(payload) {
    return Client.post(`${resource}/day-ahead-prices`, payload);
  },
  /** Generic statistics query. @param {object} payload @returns {Promise<import('axios').AxiosResponse>} */
  getGeneric(payload) {
    return Client.post(`${resource}/generic`, payload);
  },
  /** Generic spread query. @param {object} payload @returns {Promise<import('axios').AxiosResponse>} */
  getGenericSpread(payload) {
    return Client.post(`${resource}/generic/spread`, payload);
  },
  /** TenneT settlement-price moving average. @returns {Promise<import('axios').AxiosResponse>} */
  getTennetSettlementPricesMovingAverage() {
    return Client.get(`${resource}/tennet/settlementprices/moving-average`);
  },
  /** TenneT imbalance control states. @param {object} payload @returns {Promise<import('axios').AxiosResponse>} */
  getTenneTImbalanceControlStates(payload) {
    return Client.post(`${resource}/tennet/imbalance/control-states`, payload);
  },
  /** TenneT end-of-day (realtime) imbalance prices. @param {object} payload @returns {Promise<import('axios').AxiosResponse>} */
  getTennetImbalancePricesEod(payload) {
    return Client.post(`${resource}/tennet/imbalance/realtime`, payload);
  },
  /** Latest sensor readings. @returns {Promise<import('axios').AxiosResponse>} */
  getSensorData() {
    return Client.get(`${resource}/sensors`);
  },
  /** Minute-resolution sensor readings. @returns {Promise<import('axios').AxiosResponse>} */
  getSensorDataMinute() {
    return Client.get(`${resource}/sensors/60`);
  },
  /** Sensor readings for a specific selection. @param {object} payload @returns {Promise<import('axios').AxiosResponse>} */
  getSensorDataSpecific(payload) {
    return Client.post(`${resource}/sensors`, payload);
  },
  /** Multi-hub gas prices. @param {object} payload @returns {Promise<import('axios').AxiosResponse>} */
  getMultiGasPrices(payload) {
    return Client.post(`${resource}/multi/gas`, payload);
  },
  /** News items (note: hits `/news`, not `/statistics`). @param {object} [params={}] @returns {Promise<import('axios').AxiosResponse>} */
  getNews(params = {}) {
    return Client.get(`/news`, { params });
  },
  /** News source publishers for the current filter set. @param {object} [params={}] @returns {Promise<import('axios').AxiosResponse>} */
  getNewsSources(params = {}) {
    return Client.get(`/news/sources`, { params });
  },
  /** TenneT operational reports. @returns {Promise<import('axios').AxiosResponse>} */
  getTennetOperationalReports() {
    return Client.get(`${resource}/news/tennet/operational`);
  },
  /** Highlighted dashboard items. @returns {Promise<import('axios').AxiosResponse>} */
  getHighlightedItems() {
    return Client.get(`${resource}/highlighted-items`);
  },
  /** ENTSO-E negative day-ahead prices by day and year. @param {object} payload @returns {Promise<import('axios').AxiosResponse>} */
  getENTSOENegativePricesByDayAndYear(payload) {
    return Client.post(`${resource}/entsoe/day-ahead-prices/negative-by-day-and-year`, payload);
  },
};
